// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Onboarding Service
 *
 * SSE streaming for the AI onboarding workflow (run and resume), extracted
 * from the OnboardingDemo page and aligned with askHeliosService.streamAskHelios:
 * raw fetch + ReadableStream (SSE can't use the JSON api wrapper), with the
 * same 401 session-expired handling the inline version lacked.
 *
 * The event types here are AI-workflow transport shapes (gate questions,
 * classification payloads) — distinct from the coverage service contract
 * types in types/coverage.ts.
 */

import { getApiUrl, ENDPOINTS } from '../utils/constants';
import { lockIfAccountDisabled } from './api';
import { getTokenAsync } from '../config/authConfig';
import { removeUserData } from './tokenStorage';
import { useSessionGuardStore } from '../stores/sessionGuardStore';

export interface OnboardingFilePayload {
  uri: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface OnboardingRunPayload {
  site_id: string;
  files: OnboardingFilePayload[];
}

export interface GateCandidateOption {
  option_id: string;
  doc_type: string;
  confidence_score: number;
  rationale: string;
}

export interface GatePayload {
  gate_id: string;
  gate_type: string;
  workflow_instance_id: string;
  file_id: string;
  filename: string;
  question_prompt: string;
  candidate_options: GateCandidateOption[];
  allow_other: boolean;
  other_options: string[];
}

export interface GateResponsePayload {
  gate_id: string;
  workflow_instance_id: string;
  file_id: string;
  operator_response: Record<string, unknown>;
}

export interface OnboardingStreamHandlers {
  /** Signed resume context, emitted immediately before each gate event. */
  onSessionId?: (_sessionId: string) => void;
  /** HITL gate question; the stream pauses until resumed. */
  onGate?: (_gate: GatePayload) => void;
  /** Streaming text content (may accumulate into a classification payload). */
  onContent?: (_content: string) => void;
  onError: (_error: string) => void;
  onDone: () => void;
}

async function streamOnboardingEndpoint(
  endpoint: string,
  body: object,
  handlers: OnboardingStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const url = getApiUrl(endpoint);

  let token: string;
  try {
    token = await getTokenAsync();
  } catch {
    // Not a confirmed expiry — getTokenAsync already redirects to CIAM when
    // interaction is genuinely required (same rationale as askHeliosService).
    handlers.onError('Not authenticated. Please try again.');
    handlers.onDone();
    return;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    handlers.onError('Failed to connect to the onboarding workflow.');
    handlers.onDone();
    return;
  }

  if (response.status === 401) {
    removeUserData();
    useSessionGuardStore.getState().lock('unauthorized');
    return;
  }

  await lockIfAccountDisabled(response);

  if (!response.ok) {
    handlers.onError(`Onboarding workflow error (${response.status})`);
    handlers.onDone();
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    handlers.onError('Streaming not supported by browser.');
    handlers.onDone();
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) continue;

        const data = trimmed.slice(6);
        if (data === '[DONE]') {
          handlers.onDone();
          return;
        }

        try {
          const event = JSON.parse(data);
          if (event.error) {
            handlers.onError(event.error);
          } else if (event.session_id && handlers.onSessionId) {
            handlers.onSessionId(event.session_id);
          } else if (event.gate && handlers.onGate) {
            handlers.onGate(event.gate as GatePayload);
          } else if (event.content && handlers.onContent) {
            handlers.onContent(event.content);
          }
        } catch {
          // Skip malformed SSE lines
        }
      }
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    handlers.onError('Connection to the onboarding workflow lost.');
  }

  handlers.onDone();
}

/**
 * Trigger an onboarding workflow run and consume its SSE stream.
 * The stream pauses (without [DONE]) when a gate question is emitted;
 * answer it with streamOnboardingResume on the same conversation.
 */
export function streamOnboardingRun(
  payload: OnboardingRunPayload,
  handlers: OnboardingStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  return streamOnboardingEndpoint(ENDPOINTS.ONBOARDING_RUN, payload, handlers, signal);
}

/**
 * Resume a workflow paused on a gate question. The resume stream carries no
 * A further gate is preceded by a replacement session_id resume context.
 */
export function streamOnboardingResume(
  siteId: string,
  conversationId: string,
  gateResponse: GateResponsePayload | Record<string, unknown>,
  handlers: OnboardingStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  return streamOnboardingEndpoint(
    ENDPOINTS.ONBOARDING_RESUME,
    { site_id: siteId, conversation_id: conversationId, gate_response: gateResponse },
    handlers,
    signal
  );
}
